import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { RootState, AppDispatch } from '../redux/store';
import { setSolution, setLoading, setError, clearSolution } from '../redux/slices/solutionSlice';
import { Solution } from '../models/Solution';

// Helper function to convert Firestore data
const convertFirestoreData = (doc: any): Solution => {
  const data = doc.data();

  return {
    id: doc.id,
    ...data,
    createdAt:
      data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt:
      data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  } as Solution;
};

export const useSolution = (problemId: string, userId: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const solution = useSelector((state: RootState) => state.solution.solution);
  const loading = useSelector((state: RootState) => state.solution.loading);
  const error = useSelector((state: RootState) => state.solution.error);

  useEffect(() => {
    dispatch(setLoading(true));

    // Set up real-time listener for solution document
    const solutionQuery = query(
      collection(db, 'generated_documents'),
      where('problemId', '==', problemId),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(
      solutionQuery,
      snapshot => {
        if (!snapshot.empty) {
          // Get the first matching document
          const solutionData = convertFirestoreData(snapshot.docs[0]);
          dispatch(setSolution(solutionData));
        }
        dispatch(setLoading(false));
      },
      error => {
        console.error('Error fetching solution:', error);
        dispatch(setError(error.message));
        dispatch(setLoading(false));
      }
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      dispatch(clearSolution());
    };
  }, [dispatch, problemId, userId]);

  return {
    solution,
    loading,
    error,
  };
};
